import {Renderable, RenderableFunction} from '@esgi/ui-kit/core';
import {contextProtection, join} from '@esgi/ui-kit/utils';
import React, {ReactNode} from 'react';
import {Observable, Subscription} from 'rxjs';
import {tap} from 'rxjs/operators';
import {isFunction} from 'underscore';
import FormContext from '../form/form-context';
import {ElementStatus, IFormControl} from '../types';
import FormElementContext, {FormElementContextState} from './form-element-context';

import styles from './form-element.module.less';

class State {
	status: ElementStatus = ElementStatus.untouched;
	value: any;
	fieldRef: any;
}

export interface FormElementProps<T> {
	disableValidateOnBlur?: boolean;
	disableValidateOnChange?: boolean;
	className?: string;
	control: IFormControl
	children?: Renderable | RenderableFunction<FormElementContextState<T>>;
}

export class FormElement<T = any> extends React.PureComponent<FormElementProps<T>, State> {
	private controlSubscription: Subscription;
	private validateSubscription: Subscription;

	constructor(props: FormElementProps<T>) {
		super(props);
		const state = new State();
		state.value = props.control.value;
		state.status = props.control.status;
		this.state = state;
	}

	public override componentDidMount() {
		if (!this.props.control) {
			throw new Error('FormElement requires a form control.');
		}
		this.controlSubscription = this.props.control.onChanged.subscribe(v => {
			if (v.reason === 'value') {
				this.setState({value: v.currState.value});
			}

			if (v.reason === 'status') {
				this.setState({status: v.currState.status});
			}
		});
		this.setState({value: this.props.control.value, status: this.props.control.status});
	}

	public override render(): ReactNode {
		return <FormContext.Consumer>
			{(context) => {
				contextProtection(context, FormElement, FormContext);
				if (context.formGroup) {
					const control = this.props.control;
					if (control) {
						const elementContext = {
							value: this.state.value,
							status: this.state.status,
							validate: (silence?: boolean) => this.validate(silence),
							hasError: (errorType: string) => this.props.control.hasError(errorType),
							firstError: () => this.props.control.firstError(),
							setStatus: (value: ElementStatus) => this.props.control.status = value,
							setValue: (value: any) => this.updateValue(value),
							setRef: (ref: any) => this.setState({fieldRef: ref}),
							validateOnBlur: () => this.validateOnBlur(),
							ref: this.state.fieldRef,
						} as FormElementContextState<T>;
						return <FormElementContext.Provider value={elementContext}>
							<div className={join(styles.formGroup, this.props.className)}>
								{this.renderChildren(elementContext)}
							</div>
						</FormElementContext.Provider>;
					} else {
						throw new Error('[FormElement]:Control is not provided provided.');
					}
				} else {
					throw new Error('[FormElement]:FormGroup is not provided in the context above.');
				}
			}}
		</FormContext.Consumer>;
	}

	private renderChildren(context: FormElementContextState<T>): ReactNode {
		if (isFunction(this.props.children)) {
			return this.props.children(context);
		}
		return this.props.children;
	}

	public override componentWillUnmount() {
		if (this.controlSubscription) {
			this.controlSubscription.unsubscribe();
		}

		if (this.validateSubscription) {
			this.validateSubscription.unsubscribe();
		}
	}

	private updateValue(value: any): void {
		this.setState({value: value, status: ElementStatus.pending}, () => {
			this.props.control.value = value;
			if (this.props.disableValidateOnChange) {
				return;
			}

			if (this.validateSubscription) {
				this.validateSubscription.unsubscribe();
			}

			this.validateSubscription = this.validate(false).subscribe(() => {
				if (this.validateSubscription) {
					this.validateSubscription.unsubscribe();
					this.validateSubscription = null;
				}
			});
		});
	}

	private validateOnBlur(): void {
		if (this.props.disableValidateOnBlur) {
			return;
		}

		if (this.validateSubscription) {
			this.validateSubscription.unsubscribe();
		}

		this.validateSubscription = this.validate(false).subscribe(() => {
			if (this.validateSubscription) {
				this.validateSubscription.unsubscribe();
				this.validateSubscription = null;
			}
		});
	}

	private validate(silence: boolean): Observable<string[]> {
		return this.props.control.validate(silence).pipe(tap(r => this.setState({status: this.props.control.status})));
	}
}

export default FormElement;

